import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PaperPlaneRight, CaretLeft } from "@phosphor-icons/react";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChatThread() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [contact, setContact] = useState(null);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/chat/messages?with=${userId}`).then((r) => setMessages(r.data));
    api.get("/chat/contacts").then((r) => setContact(r.data.find((c) => c.id === userId) || null));
  }, [userId]);

  useEffect(() => {
    if (!user) return;
    const ws = new WebSocket(wsUrl(user.id));
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const payload = JSON.parse(e.data);
      if (payload.type === "message") {
        const m = payload.data;
        if ((m.sender_id === userId && m.receiver_id === user.id) || (m.sender_id === user.id && m.receiver_id === userId)) {
          setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
        }
      }
    };
    return () => ws.close();
  }, [user, userId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ receiver_id: userId, text: t }));
    } else {
      api.post("/chat/messages", { receiver_id: userId, text: t }).then((r) =>
        setMessages((prev) => (prev.some((x) => x.id === r.data.id) ? prev : [...prev, r.data]))
      );
    }
    setText("");
  };

  return (
    <div className="flex flex-col h-full">
      <header className="shrink-0 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-stone-200/60 px-4 py-3 flex items-center gap-3">
        <button data-testid="chat-back" onClick={() => navigate("/pesan")} className="w-9 h-9 rounded-full bg-white border border-stone-200 flex items-center justify-center">
          <CaretLeft size={18} weight="bold" className="text-stone-700" />
        </button>
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="font-heading font-bold text-primary">{contact?.name?.[0] || "?"}</span>
        </div>
        <div>
          <p className="font-semibold text-stone-800 text-sm">{contact?.name || "Chat"}</p>
          <p className="text-green-600 text-xs">● Online</p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-2">
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 text-sm shadow-sm ${mine ? "bg-primary text-white rounded-2xl rounded-tr-sm" : "bg-white border border-stone-100 text-stone-700 rounded-2xl rounded-tl-sm"}`}>
                {m.text}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-stone-400"}`}>
                  {new Date(m.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        {messages.length === 0 && <p className="text-center text-stone-400 text-sm mt-10">Mulai percakapan 👋</p>}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-stone-200/60 bg-white px-3 py-3 flex items-center gap-2 shrink-0">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ketik pesan..."
          className="flex-1 bg-stone-100 rounded-full py-3 px-4 outline-none text-sm focus:ring-2 focus:ring-primary/30"
        />
        <button data-testid="chat-send" onClick={send} className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center active:scale-90 transition-transform">
          <PaperPlaneRight size={20} weight="fill" />
        </button>
      </div>
    </div>
  );
}
